/* Welcome-offer email popup: validates the address, subscribes the customer, applies the discount codes. */
(() => {
  const root = document.querySelector('[data-email-popup]');
  if (!root || window.Shopify?.designMode) return;

  const CODES = (root.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean);
  const DELAY = Number(root.dataset.delay || 8) * 1000;
  const KEY = 'pb_email_popup';
  const DISMISS_DAYS = 2;
  const MAX_RETURN_VISITS = 5; // later visits on which the welcome codes are re-applied
  const KEEP_DAYS = 14; // ...and never after this many days

  const store = {
    get() {
      try {
        return JSON.parse(localStorage.getItem(KEY) || '{}');
      } catch (e) {
        return {};
      }
    },
    set(v) {
      try {
        localStorage.setItem(KEY, JSON.stringify(v));
      } catch (e) {
        /* ignore */
      }
    },
  };

  const state = store.get();
  const path = location.pathname;
  const skipPage = /^\/(cart|checkout|password)/.test(path) || path.includes('/account');

  const applyCodes = () => {
    if (!CODES.length) return Promise.resolve();
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: CODES.join(',') }),
    }).catch(() => {});
  };

  // Returning subscriber: re-apply the codes for a few more visits (or until they expire), never show the popup again.
  if (state.subscribed) {
    const visits = (state.visits || 0) + 1;
    const active = visits <= MAX_RETURN_VISITS && Date.now() - state.at < KEEP_DAYS * 864e5;
    store.set({ ...state, visits });
    if (active) applyCodes();
    return;
  }
  if (skipPage) return;
  if (state.dismissedAt && Date.now() - state.dismissedAt < DISMISS_DAYS * 864e5) return;

  const dialog = root.querySelector('dialog');
  const form = root.querySelector('form');
  const input = form.querySelector('input[type="email"]');
  const button = form.querySelector('button[type="submit"]');
  const msg = root.querySelector('[data-popup-msg]');
  const stepForm = root.querySelector('[data-step="form"]');
  const stepDone = root.querySelector('[data-step="done"]');

  const DISPOSABLE = new Set(
    `mailinator.com guerrillamail.com guerrillamail.net guerrillamail.org guerrillamail.biz guerrillamail.de grr.la sharklasers.com guerrillamailblock.com pokemail.net spam4.me 10minutemail.com 10minutemail.net 10minutemail.org 10minutemail.co.uk 10minemail.com 20minutemail.com 30minutemail.com temp-mail.org temp-mail.io tempmail.com tempmail.net tempmail.dev tempmail.plus tempmailo.com tempail.com tempr.email temporary-mail.net throwawaymail.com trashmail.com trashmail.net trashmail.de trash-mail.com yopmail.com yopmail.net yopmail.fr cool.fr.nf jetable.fr.nf nospam.ze.tc nomail.xl.cx mega.zik.dj speed.1s.fr courriel.fr.nf moncourrier.fr.nf monemail.fr.nf monmail.fr.nf getnada.com nada.email dispostable.com maildrop.cc mailnesia.com mailcatch.com mintemail.com mohmal.com fakeinbox.com fakemail.net fakemailgenerator.com emailondeck.com getairmail.com anonbox.net anonymbox.com burnermail.io discard.email discardmail.com discardmail.de spamgourmet.com spambox.us spamex.com spamfree24.org spamhole.com harakirimail.com incognitomail.com mailexpire.com mailforspam.com mailmoat.com mailnull.com mailsac.com mytemp.email mytrashmail.com no-spam.ws objectmail.com owlymail.com proxymail.eu rcpt.at safetymail.info sogetthis.com spamavert.com spamgoes.in tempinbox.com tempmailaddress.com tempomail.fr thankyou2010.com trbvm.com wegwerfmail.de wegwerfmail.net wegwerfmail.org zoemail.org 33mail.com armyspy.com cuvox.de dayrep.com einrot.com fleckens.hu gustr.com jourrapide.com rhyta.com superrito.com teleworm.us emailfake.com email-fake.com crazymailing.com inboxkitten.com luxusmail.org moakt.com moakt.cc mail.tm mail7.io tmail.ws tmails.net tmpmail.org tmpmail.net fexbox.org fexpost.com mailtemp.info emltmp.com dropmail.me 10mail.org linshiyouxiang.net byom.de fivemail.de spamdecoy.net zetmail.com tmpeml.com`.split(
      /\s+/
    )
  );

  const TYPOS = {
    'gmial.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gmail.con': 'gmail.com',
    'gamil.com': 'gmail.com',
    'gnail.com': 'gmail.com',
    'gmail.co': 'gmail.com',
    'gmail.cm': 'gmail.com',
    'hotmial.com': 'hotmail.com',
    'hotmail.con': 'hotmail.com',
    'hotnail.com': 'hotmail.com',
    'yaho.com': 'yahoo.com',
    'yahoo.con': 'yahoo.com',
    'yahooo.com': 'yahoo.com',
    'outlok.com': 'outlook.com',
    'outlook.con': 'outlook.com',
    'iclod.com': 'icloud.com',
    'icloud.con': 'icloud.com',
  };

  const say = (text, ok) => {
    msg.textContent = text;
    msg.dataset.state = ok ? 'ok' : 'error';
  };

  const dns = async (name, type) => {
    const res = await fetch(`https://cloudflare-dns.com/dns-query?name=${encodeURIComponent(name)}&type=${type}`, {
      headers: { accept: 'application/dns-json' },
      signal: AbortSignal.timeout(4000),
    });
    if (!res.ok) throw new Error('dns');
    return res.json();
  };

  // True if the domain can receive mail. Fails open if DNS lookup itself is unavailable.
  const domainCanReceiveMail = async (domain) => {
    try {
      const mx = await dns(domain, 'MX');
      if (mx.Status === 3) return false; // NXDOMAIN
      if ((mx.Answer || []).some((a) => a.type === 15 && a.data && !/^0\s+\.?$/.test(a.data.trim()))) return true;
      // No MX: a domain can still receive mail on its A record (RFC 5321).
      const a = await dns(domain, 'A');
      return (a.Answer || []).some((r) => r.type === 1);
    } catch (e) {
      return true;
    }
  };

  const validate = async (raw) => {
    const email = raw.trim().toLowerCase();
    if (!/^[a-z0-9._%+-]+@[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(email) || email.includes('..')) {
      return { ok: false, text: 'Please enter a valid email address.' };
    }
    const domain = email.split('@')[1];
    if (TYPOS[domain]) return { ok: false, text: `Did you mean ${email.split('@')[0]}@${TYPOS[domain]}?` };
    const base2 = domain.split('.').slice(-2).join('.');
    if (DISPOSABLE.has(domain) || DISPOSABLE.has(base2)) {
      return { ok: false, text: "Disposable email addresses can't be used. Please use your real email." };
    }
    if (!(await domainCanReceiveMail(domain))) {
      return { ok: false, text: `We can't find a mail server for "${domain}". Please check the spelling.` };
    }
    return { ok: true, email };
  };

  // Scratch card: the gift is the same for everyone; scratching (or the button) just reveals it and shows the sign-up form.
  const scratch = root.querySelector('[data-scratch]');
  const canvas = root.querySelector('[data-scratch-canvas]');
  const claim = root.querySelector('[data-claim]');
  const titleEl = root.querySelector('[data-popup-title]');
  const textEl = root.querySelector('[data-popup-text]');
  const REVEAL_AT = 0.45; // share of the card that must be scratched off
  let scratchReady = false;

  const reveal = () => {
    if (scratch.dataset.revealed) return;
    scratch.dataset.revealed = 'true';
    claim.hidden = false;
    titleEl.textContent = 'Your gift is ready!';
    textEl.textContent = 'Join Bloom Club to add it to your cart.';
  };

  const initScratch = () => {
    if (scratchReady || scratch.dataset.revealed) return;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width) return;
    scratchReady = true;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.scale(dpr, dpr);

    const foil = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    foil.addColorStop(0, '#e9dde4');
    foil.addColorStop(0.5, '#fbf6f9');
    foil.addColorStop(1, '#cdb6c4');
    ctx.fillStyle = foil;
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.fillStyle = '#7a4a63';
    ctx.font = '700 16px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SCRATCH HERE', rect.width / 2, rect.height / 2);

    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineWidth = 34;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let last = null;
    const point = (e) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const cleared = () => {
      const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      let clear = 0;
      let total = 0;
      for (let y = 0; y < height; y += 6) {
        for (let x = 0; x < width; x += 6) {
          total++;
          if (data[(y * width + x) * 4 + 3] < 128) clear++;
        }
      }
      return clear / total;
    };

    canvas.addEventListener('pointerdown', (e) => {
      canvas.setPointerCapture(e.pointerId);
      last = point(e);
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(last.x + 0.1, last.y);
      ctx.stroke();
    });
    canvas.addEventListener('pointermove', (e) => {
      if (!last) return;
      const p = point(e);
      ctx.beginPath();
      ctx.moveTo(last.x, last.y);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
      last = p;
    });
    const end = () => {
      if (!last) return;
      last = null;
      if (cleared() >= REVEAL_AT) reveal();
    };
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
  };

  // Keyboard users can't scratch: Enter or Space on the card reveals it.
  canvas.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      reveal();
    }
  });

  let retryTimer = null;
  const open = () => {
    if (dialog.open) return;
    // Don't cover the cart drawer, quick add or search while the shopper is
    // mid-purchase — try again a few seconds later instead.
    if (document.querySelector('dialog[open]')) {
      if (!retryTimer) {
        retryTimer = setTimeout(() => {
          retryTimer = null;
          open();
        }, 4000);
      }
      return;
    }
    dialog.showModal();
    initScratch();
  };
  const dismiss = () => {
    if (!store.get().subscribed) store.set({ ...store.get(), dismissedAt: Date.now() });
    dialog.close();
  };

  root.querySelectorAll('[data-popup-close]').forEach((el) => el.addEventListener('click', dismiss));
  dialog.addEventListener('cancel', (e) => {
    e.preventDefault();
    dismiss();
  });
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dismiss();
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    button.disabled = true;
    say('Checking your email…', true);
    const v = await validate(input.value);
    if (!v.ok) {
      say(v.text, false);
      button.disabled = false;
      return;
    }

    try {
      input.value = v.email;
      const res = await fetch(form.action, { method: 'POST', body: new FormData(form), redirect: 'follow' });
      if (!res.ok) throw new Error(String(res.status));
    } catch (err) {
      say('Something went wrong. Please try again.', false);
      button.disabled = false;
      return;
    }

    store.set({ subscribed: true, email: v.email, at: Date.now() });
    await applyCodes();
    stepForm.hidden = true;
    stepDone.hidden = false;
  });

  root.querySelector('[data-popup-shop]')?.addEventListener('click', () => dialog.close());

  const schedule = () => setTimeout(open, DELAY);
  if (document.readyState === 'complete') schedule();
  else window.addEventListener('load', schedule, { once: true });

  // Desktop exit intent
  document.addEventListener('mouseout', (e) => {
    if (e.clientY <= 0 && !e.relatedTarget) open();
  });
})();
